import { useRef, type FC, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { ProductPrice } from './Details/ProductPrice';
import { ProductShipping } from './Details/ProductShipping';
import { ProductTitle } from './Details/ProductTitle';
import { ProductDetails } from './Details/ProductDetails';

import { TILE_SIZES, type TileSize } from './constants';
import { type MeasurementUnit } from 'dibs-intl/exports/units';

import dibsCss from 'dibs-css';
import styles from './styles/styles.scss';

import { type ProductBaseTile_viewer$key } from './__generated__/ProductBaseTile_viewer.graphql';
import { type ProductBaseTile_item$key } from './__generated__/ProductBaseTile_item.graphql';
import { type ProductBaseTile_user$key } from './__generated__/ProductBaseTile_user.graphql';

const viewerFragment = graphql`
    fragment ProductBaseTile_viewer on Viewer {
        ...ProductShipping_viewer
    }
`;

const itemFragment = graphql`
    fragment ProductBaseTile_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
    ) {
        ...ProductTitle_item
        ...ProductPrice_item
            @arguments(
                isTrade: $isTrade
                pageDisplayEnum: $pageDisplayEnum
                priceBookName: $priceBookName
            )
        ...ProductShipping_item
        ...ProductDetails_item
    }
`;

const userFragment = graphql`
    fragment ProductBaseTile_user on User {
        ...ProductDetails_user
    }
`;

type Props = {
    viewer: ProductBaseTile_viewer$key | null | undefined;
    item: ProductBaseTile_item$key | null | undefined;
    user: ProductBaseTile_user$key | null | undefined;
    afterDisplay: boolean;
    beforeRender?: () => void;
    currency: string;
    children: ReactElement;
    isSponsored?: boolean;
    index: number;
    onContentVisible?: () => void;
    size: TileSize;
    showBrowseSimilar?: boolean;
    showMeasurements?: boolean;
    showPrice?: boolean;
    measurementUnit?: MeasurementUnit;
    favoritesComponent?: ReactElement | null;
};

export const ProductBaseTile: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    afterDisplay,
    beforeRender,
    currency,
    children,
    isSponsored,
    onContentVisible,
    size,
    showBrowseSimilar,
    showMeasurements,
    showPrice,
    measurementUnit,
    favoritesComponent,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const tileRef = useRef(null);

    if (!item) {
        return null;
    }

    return (
        <article
            className={classnames(
                styles.itemContainer,
                dibsCss.relative,
                dibsCss.boxBorder,
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.wFull,
                dibsCss.minW0,
                {
                    [styles.tileSmall]: size === TILE_SIZES.small,
                    [styles.tileMedium]: size === TILE_SIZES.medium,
                    [dibsCss.h400px]: size === TILE_SIZES.large,
                    [dibsCss.hFull]: size === TILE_SIZES.auto,
                }
            )}
            ref={tileRef}
        >
            {typeof beforeRender === 'function' && (
                <VisibilityTracker
                    elementRef={tileRef}
                    onVisibilityChange={({ isVisible }) => (isVisible ? beforeRender() : undefined)}
                    observerOptions={{ rootMargin: `100% 0px` }}
                />
            )}
            {typeof onContentVisible === 'function' && (
                <VisibilityTracker
                    elementRef={tileRef}
                    onVisibilityChange={({ isVisible }) =>
                        isVisible ? onContentVisible() : undefined
                    }
                    observerOptions={{ threshold: 0.5 }}
                />
            )}
            {children}
            <div className={classnames(dibsCss.wFull, dibsCss.hFull, dibsCss.mtXsmall)}>
                <ProductTitle item={item} isSponsored={isSponsored} />

                {showPrice ? (
                    <ProductPrice currency={currency} item={item} />
                ) : (
                    <p
                        className={classnames(
                            styles.pricePlaceHolder,
                            dibsCss.wFull,
                            dibsCss.mtXxsmall
                        )}
                    />
                )}
                <ProductShipping viewer={viewer} item={item} currency={currency} />
                <ProductDetails
                    item={item}
                    user={user}
                    afterDisplay={afterDisplay}
                    isSponsored={isSponsored}
                    measurementUnit={measurementUnit}
                    showBrowseSimilar={showBrowseSimilar}
                    showMeasurements={showMeasurements}
                />
            </div>
            {favoritesComponent}
        </article>
    );
};
